/**
 * YjsDocManager - Manages Yjs documents for CRDT-based sync
 *
 * Handles conversion between the app's tree structure and Yjs Y.Map,
 * and provides methods for syncing changes.
 */

import * as Y from "yjs";
import { createDocPersistence } from "../storage/index.js";
// Lazy facade — the real serializer drags the TipTap/ProseMirror stack, which
// must stay out of the boot bundle. CRDTBridge.init() guarantees the chunk is
// loaded before any yxml-v1 doc reaches toTree()/note writes.
import { fragmentToMarkdown, fragmentToHtml, markdownToFragment } from "./notes-serializer-facade.js";
import * as StorageHealth from "./storage-health.js";
import { getShareRole } from "./share-state.js";
import {
  isLocalTxOrigin,
  REMOTE_ORIGIN,
  USER_ORIGIN,
  IMPORT_ORIGIN,
  MIGRATION_ORIGIN,
  CONSISTENCY_ORIGIN,
  ORPHAN_REPAIR_ORIGIN,
  SESSION_ORIGIN,
  LAYOUT_ORIGIN,
} from "./origins.js";
import { markChanged } from "./unsynced-changes.js";
import { ownSigningKey, signSession } from "../crypto/signing-key.js";
import {
  SCHEMA_V2,
  REPORTED_V2_WITHOUT_OLD_COPY,
  LOCAL_FIELDS,
  placeV1,
  readMap,
  EDIT_SESSIONS,
  DELETED_DOC_NOTES,
  treeNodeToFlat,
  flatNodeToTree,
} from "./map-format.js";
import { keysBetween, isValidOrderKey } from "./order-key.js";

import { MapNodesV2 } from "./map-nodes-v2.js";
import { LocalNodeState } from "./local-node-state.js";
import { trackMapSize } from "../services/map-size.js";

/**
 * YjsDocManager class
 * Manages a Yjs document for a single map/document
 */
export class YjsDocManager {
  constructor(docId, options = {}) {
    this.docId = docId;
    this.doc = new Y.Doc();
    this.nodes = this.doc.getMap("nodes");
    this.metadata = this.doc.getMap("metadata");
    this.docNotes = this.doc.getArray("docNotes"); // Y.Array for conflict-free notes
    this.deletedDocNotes = this.doc.getMap(DELETED_DOC_NOTES); // ids of deleted ones (map-format.js)
    this.persistence = null;
    this._stopPersistenceMonitor = null;
    this.onUpdate = options.onUpdate || null;
    this.onOrphansDetected = options.onOrphansDetected || null;
    this.userId = options.userId || null;
    // The open map and its linked maps (CRDTBridge) count against the size
    // limit; copies made for exports, conversions or sending do not.
    this._stopSizeWatch = options.watchSize ? trackMapSize(docId, this.doc) : null;

    // Schema v2 (map-format.js): one key per field, place in the node. A
    // map is v2 once `metadata.schemaVersion` is 2 (migrateToV2, or created
    // so); every method below reads and writes the schema the map has.
    this.v2 = new MapNodesV2(this);
    // Per-device fields of a v2 map (collapsed, measured sizes).
    this.local = new LocalNodeState(docId, { persist: options.persistLocal !== false });
    // v1 changed since the last fold of late v1 changes (MapNodesV2.foldLateV1).
    this._v1Changed = true;
    this.nodes.observe(() => { this._v1Changed = true; });

    // Track if we're currently making local changes (to avoid triggering onUpdate)
    this._isLocalChange = false;

    // Notes fragment cache: only re-serialize fragments that changed.
    // Used by getTree() to attach markdown onto tree nodes — see the
    // notesFormat==='yxml-v1' branch around line 400.
    this._notesCache = new Map();  // nodeId → markdown string
    this._dirtyNotes = new Set();  // nodeIds with changed fragments
    this._notesObservers = new Map(); // nodeId → observer function

    // Note: doc-notes do NOT need a parallel cache/observer trio. Unlike
    // node notes — which are attached onto tree-node objects during
    // getTree's rebuild and benefit from cached serialization — doc
    // notes are the Y.Array entries themselves. Callers read each
    // docNote's body via serializeDocNote (one fragment at a time, on
    // demand). Adding a cache layer would mean extra observer setup
    // + invalidation logic for no avoided work.

    // A change made here: not on the server until it confirms it
    // (unsynced-changes.js; sign-out names what it would lose).
    this.doc.on("update", (update, origin) => {
      if (isLocalTxOrigin(origin)) markChanged(this.docId);
    });

    // Who made a change (map history): the server records the editing
    // session each change arrives on (setEditSession); the map says, signed,
    // which account that session is (map-format.js `editSessions`).
    this.editSession = null;
    this._wroteHere = false;
    this._named = null;
    this.doc.on("afterTransaction", (transaction) => this._afterLocalChange(transaction));
  }

  /** A change made here (not layout sizes, not the session entry itself). */
  _afterLocalChange(transaction) {
    const origin = transaction.origin;
    if (!transaction.local || origin === SESSION_ORIGIN || origin === LAYOUT_ORIGIN || !isLocalTxOrigin(origin)) return;
    const client = this.doc.clientID;
    const wrote = (transaction.afterState.get(client) || 0) > (transaction.beforeState.get(client) || 0)
      || transaction.deleteSet.clients.size > 0;
    if (!wrote) return;
    this._wroteHere = true;
    void this._nameSession();
  }

  /**
   * The editing session the server records this map's changes under (its
   * sync answer; null from a server older than history, or for a reader).
   * Changes made on this page name it at once. Earlier work this copy still
   * has to send is named by the reconnect step, only if it sends something
   * (nameEditSession). Named on the "not confirmed yet" mark alone, the
   * entry could be written after the server had confirmed everything and
   * sign-out's last sync had closed: the map was then listed as having
   * changes not on the server.
   * @param {number|null} session
   */
  setEditSession(session) {
    this.editSession = Number.isSafeInteger(session) && session > 0 ? session : null;
    if (this._wroteHere) void this._nameSession();
  }

  /**
   * Something is sent from here that no local change made (the reconnect
   * step's push of what the server lacks): its session is named too, or
   * the history shows it as nobody's (simulation, 2026-10-26).
   */
  nameEditSession() {
    this._wroteHere = true;
    return this._nameSession();
  }

  /**
   * Say in the map, signed with this account's signing key, that this
   * editing session (and Yjs client) is this account: once per session.
   * Not for a viewer (nothing of theirs is saved).
   */
  async _nameSession() {
    const session = this.editSession;
    if (!session || getShareRole(this.docId) === "viewer") return;
    const client = this.doc.clientID;
    const name = `${session}:${client}`;
    if (this._named === name) return;
    this._named = name;
    let key = null;
    try { key = await ownSigningKey(); } catch (error) { console.warn("[YJS] No signing key:", error?.message); }
    if (!key || this.editSession !== session || this.doc.clientID !== client) {
      if (this._named === name) this._named = null;
      return;
    }
    const sessions = this.doc.getMap(EDIT_SESSIONS);
    if (sessions.get(name)?.user === key.userId) return;
    this.doc.transact(() => sessions.set(name, { user: key.userId, signature: signSession(key, session, client) }), SESSION_ORIGIN);
  }

  /**
   * Initialize IndexedDB persistence for offline support
   */
  async initPersistence() {
    if (this.persistence) return;

    this.persistence = createDocPersistence(this.docId, this.doc, { kind: "main" });

    await StorageHealth.waitForIndexedDBSync(this.persistence, `orbital-${this.docId}`);
    console.log(`[YjsDocManager] Loaded from IndexedDB: ${this.docId}`);
    this._stopPersistenceMonitor = StorageHealth.monitorIndexedDBPersistence(
      this.persistence,
      `orbital-${this.docId}`,
    );
  }

  /**
   * Load a tree structure into the Yjs document
   * @param {Object} rootNode - Root of the tree structure
   */
  fromTree(rootNode, options = {}) {
    const existingSize = this.nodeCount();
    console.log(
      "[YJS] fromTree called, root:",
      rootNode?.name,
      "nodeCount:",
      this._countNodes(rootNode),
      "existingCRDTSize:",
      existingSize,
    );
    // Guard: fromTree does nodes.clear() + repopulate, which emits delete-deltas
    // for every existing node. That's correct for imports (replaceTree) and
    // fresh-doc init (syncFullTree on an empty doc), but a regression if it
    // fires mid-session on a populated doc — it propagates mass deletes to
    // peers. Log loudly with a stack so the offending caller is obvious.
    //
    // Callers that legitimately replace a populated doc (imports via
    // replaceTree) pass { isReplace: true } to opt out of the warning.
    if (existingSize > 0 && !options.isReplace) {
      console.error(
        `[YJS] fromTree called on a POPULATED doc (${existingSize} nodes). ` +
        `This will emit delete-deltas for every existing node. If this is an ` +
        `import, that is intentional; otherwise it is a bug. Stack:`,
        new Error().stack,
      );
    }
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        // A new map can start in v2 (options.schema, from the code that
        // creates it); any other empty doc may have v1 content on the server
        // not synced yet, so it starts v1 and is migrated once synced.
        const newV2 = options.schema === SCHEMA_V2 && existingSize === 0 && !this.metadata.get("rootId");
        if (this.isV2() || newV2) {
          if (newV2) this.metadata.set("schemaVersion", SCHEMA_V2);
          this.v2.replaceTree(rootNode, {}, { created: newV2 });
          return;
        }

        // Clear existing nodes
        this.nodes.clear();

        // Recursively add all nodes and initialize Y.XmlFragment for notes
        const addNode = (node, parentId) => {
          const flatNode = treeNodeToFlat(node, parentId);
          // If notesFormat is yxml-v1, store notes in fragment, not flat node
          const notesStr = typeof node.notes === 'string' ? node.notes : '';
          if (this.metadata.get('notesFormat') === 'yxml-v1' && notesStr.trim()) {
            flatNode.notes = ''; // clear from flat node
            this.setNodeNotesFromMarkdown(node.id, notesStr);
          }
          this.nodes.set(node.id, flatNode);

          (node.children || []).forEach((child) => {
            addNode(child, node.id);
          });
        };

        addNode(rootNode, null);

        // Store root ID in metadata
        this.metadata.set("rootId", rootNode.id);
      }, IMPORT_ORIGIN); // Full-tree replace — not undoable
      console.log("[YJS] fromTree complete, nodes:", this.nodeCount());
    } finally {
      this._isLocalChange = false;
    }
  }

  /**
   * Additively merge a tree into the CRDT without clearing existing nodes.
   * Only adds nodes that don't already exist in the CRDT, preserving any
   * CRDT-only nodes that may not be in the Store.
   * @param {Object} rootNode - Root of the tree structure to merge
   */
  mergeTreeAdditive(rootNode) {
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        if (this.isV2()) {
          this.v2.mergeAdditive(rootNode, this._stamp());
          return;
        }
        const mergeNode = (node, parentId) => {
          if (!this.nodes.has(node.id)) {
            const flatNode = treeNodeToFlat(node, parentId);
            flatNode._lastEditedBy = this.userId;
            flatNode._lastEditedAt = Date.now();
            // Initialize Y.XmlFragment for notes if in yxml-v1 mode
            const notesStr = typeof node.notes === 'string' ? node.notes : '';
            if (this.metadata.get('notesFormat') === 'yxml-v1' && notesStr.trim()) {
              flatNode.notes = '';
              this.setNodeNotesFromMarkdown(node.id, notesStr);
            }
            this.nodes.set(node.id, flatNode);

            // Update parent's childIds if the parent exists in CRDT
            if (parentId) {
              const parent = this.nodes.get(parentId);
              if (parent && parent.childIds) {
                const childIds = [...parent.childIds];
                if (!childIds.includes(node.id)) {
                  childIds.push(node.id);
                  this.nodes.set(parentId, { ...parent, childIds });
                }
              }
            }
          }

          (node.children || []).forEach((child) => {
            mergeNode(child, node.id);
          });
        };

        mergeNode(rootNode, null);
      }, CONSISTENCY_ORIGIN);
      console.log("[YJS] mergeTreeAdditive complete, nodes:", this.nodeCount());
    } finally {
      this._isLocalChange = false;
    }
  }

  // NOTE: resetWithTree() was removed (audit, 2026-06-12). It was dead code
  // with a latent bug: it attached IndexeddbPersistence to the existing
  // `orbital-<docId>` store without clearing it, so y-indexeddb merged the
  // old persisted state back into the "fresh" doc — exactly the state the
  // caller meant to replace. If a full-reset import is ever needed again,
  // clear the IDB store first.

  _countNodes(node) {
    if (!node) return 0;
    let count = 1;
    (node.children || []).forEach((c) => (count += this._countNodes(c)));
    return count;
  }

  /**
   * Where each node shows in the tree: one place per node, never a cycle.
   * v1: map-format.js `placeV1` (the 2026-10-02 safeguards; orphans are
   * written back by toTree). v2: `placeV2`, nothing to write back.
   * `removeNode` deletes what this shows under a node, nothing else.
   * @returns {{children: Map<string, string[]>, orphans: Array<{id: string, parentId: string}>}}
   *   children: node → its shown children, in order
   */
  _placement() {
    if (this.isV2()) return { children: this.v2.state().place.children, orphans: [] };
    return placeV1(this.nodes, this.metadata.get("rootId"));
  }

  /**
   * Convert Yjs document back to tree structure
   * @returns {Object} Root node of tree structure
   */
  toTree() {
    const rootId = this.metadata.get("rootId");
    if (!rootId) return null;

    const nodeMap = this.isV2() ? this._treeNodesV2() : this._treeNodesV1(rootId);
    this._attachNotes(nodeMap);
    return nodeMap.get(rootId) || null;
  }

  /** v1: every node, connected as `_placement` shows them; orphans written back. */
  _treeNodesV1(rootId) {
    // Build a map of all nodes
    const nodeMap = new Map();
    this.nodes.forEach((flatNode, id) => {
      nodeMap.set(id, flatNodeToTree(flatNode));
    });

    // Connect each node to the children shown under it (one place per node,
    // no cycle; orphans re-attached to preserve data: _placement).
    const { children, orphans } = this._placement();
    nodeMap.forEach((node, id) => {
      node.children = (children.get(id) || []).map((childId) => nodeMap.get(childId)).filter(Boolean);
    });

    if (orphans.length > 0) {
      console.warn(`[YJS] toTree: ${orphans.length} orphaned node(s) detected — re-attaching to preserve data`);
      for (const { id, parentId } of orphans) {
        console.warn(`[YJS]   re-attached "${nodeMap.get(id)?.name}" (${id}) → parent ${parentId}`);
      }

      // Fix the CRDT so orphans don't recur on next toTree() call.
      // Uses "orphan-repair" origin so the CRDT observer skips the redundant rebuild
      // (the tree we're about to return already has orphans re-attached).
      // Not as a viewer: the server refuses the write, and the delta that is
      // never acknowledged wedges sync ("Saving stopped"). The returned tree
      // is already repaired; members with write access fix the CRDT.
      if (getShareRole(this.docId) !== "viewer") this._repairOrphans(orphans, rootId);
    }
    return nodeMap;
  }

  /**
   * v2: the nodes shown (not deleted with a parent), with this device's
   * fields. v1 changes no migration saw are folded in first (not as a
   * viewer: the server refuses the write; members with write access do it).
   */
  _treeNodesV2() {
    if (this._v1Changed && getShareRole(this.docId) !== "viewer") {
      this._v1Changed = false;
      this.foldLateV1();
    }
    const { flat, place } = this.v2.state();
    const nodeMap = new Map();
    for (const [id, fields] of flat) {
      if (!place.hidden.has(id)) nodeMap.set(id, flatNodeToTree({ ...fields, ...this._localFields(id) }));
    }
    nodeMap.forEach((node, id) => {
      node.children = (place.children.get(id) || []).map((childId) => nodeMap.get(childId)).filter(Boolean);
    });
    return nodeMap;
  }

  /**
   * This device's fields of a v2 node; where it has none, what the map held
   * before it moved to v2 (the untouched v1 copy).
   */
  _localFields(id) {
    const local = this.local.get(id);
    const before = this.nodes.get(id);
    const fields = {};
    for (const field of LOCAL_FIELDS) {
      const value = local && local[field] !== undefined ? local[field] : before?.[field];
      if (value !== undefined) fields[field] = value;
    }
    return fields;
  }

  /**
   * A node's notes fragment, or null when the map has none: never creates
   * one. Getting a missing fragment creates it, and creating one (or typing
   * one received from another device) opens a transaction. The tree build
   * runs inside the observers of a change, and did that for every node
   * without notes: converting the 5,000 plain-text notes of an open
   * 20,000-item map queued 15,000 transactions, and Yjs, cleaning them up
   * one inside the other, overflowed the stack (2026-10-15).
   * @param {string} id
   * @returns {Y.XmlFragment|null}
   */
  _notesFragmentIfAny(id) {
    return this.doc.share.has('notes-' + id) ? this.doc.getXmlFragment('notes-' + id) : null;
  }

  /** Notes from the Y.XmlFragments (yxml-v1 maps) onto the tree nodes. */
  _attachNotes(nodeMap) {
    // Dual-read: prefer Y.XmlFragment notes over flat node string.
    // Uses dirty-tracking cache: only re-serialize fragments that changed.
    const notesFormat = this.metadata.get('notesFormat');
    if (notesFormat === 'yxml-v1') {
      // Notes received from another device arrive as untyped fragments, and
      // typing one opens a transaction: all of them in one.
      const untyped = [];
      nodeMap.forEach((_, id) => {
        const type = this.doc.share.get('notes-' + id);
        if (type && !(type instanceof Y.XmlFragment)) untyped.push(id);
      });
      if (untyped.length > 0) {
        this.doc.transact(() => {
          for (const id of untyped) this.doc.getXmlFragment('notes-' + id);
        });
      }

      // Re-serialize only dirty fragments, use cache for the rest
      for (const dirtyId of this._dirtyNotes) {
        const fragment = this._notesFragmentIfAny(dirtyId);
        if (fragment && fragment.length > 0) {
          try {
            this._notesCache.set(dirtyId, this.serializeNodeNotes(dirtyId));
          } catch (err) {
            console.warn('[YJS] Failed to serialize notes fragment for', dirtyId, err);
          }
        } else {
          this._notesCache.delete(dirtyId);
        }
      }
      this._dirtyNotes.clear();

      // Apply cached notes to tree nodes
      nodeMap.forEach((treeNode, id) => {
        const cached = this._notesCache.get(id);
        if (cached !== undefined) {
          treeNode.notes = cached;
        } else {
          // First time: serialize and cache
          const fragment = this._notesFragmentIfAny(id);
          if (fragment && fragment.length > 0) {
            try {
              const md = this.serializeNodeNotes(id);
              this._notesCache.set(id, md);
              treeNode.notes = md;
              // Set up observer for future dirty-tracking
              this._observeFragment(id);
            } catch (err) {
              console.warn('[YJS] Failed to serialize notes fragment for', id, err);
            }
          }
        }
      });
    }
  }

  /**
   * Repair orphaned nodes in the CRDT by adding them to their parent's childIds.
   * Called by toTree() when orphans are detected.
   * @param {Array<{id: string, parentId: string}>} orphans - each orphan and the
   *   shown parent it was re-attached to (its parentId if shown, else the root)
   * @param {string} rootId - Root node ID (fallback parent)
   */
  _repairOrphans(orphans, rootId) {
    const orphanIds = orphans.map((orphan) => orphan.id);
    this._isLocalChange = true;
    try {
      this.doc.transact(() => {
        for (const { id: orphanId, parentId } of orphans) {
          const flatNode = this.nodes.get(orphanId);
          if (!flatNode) continue;

          const targetParentId = parentId && this.nodes.has(parentId) ? parentId : rootId;

          const parent = this.nodes.get(targetParentId);
          if (!parent) continue;

          const childIds = [...(parent.childIds || [])];
          if (!childIds.includes(orphanId)) {
            childIds.push(orphanId);
            this.nodes.set(targetParentId, { ...parent, childIds });
          }

          // Fix parentId if we had to fall back to root
          if (targetParentId !== flatNode.parentId) {
            this.nodes.set(orphanId, { ...flatNode, parentId: targetParentId });
          }
        }
      }, ORPHAN_REPAIR_ORIGIN);
      console.warn(`[YJS] Repaired ${orphanIds.length} orphaned node(s) in CRDT`);
    } finally {
      this._isLocalChange = false;
    }

    // Notify the app so it can show a warning to the user
    if (this.onOrphansDetected) {
      this.onOrphansDetected(orphanIds.length);
    }
  }

  /**
   * Update a single node
   * @param {string} nodeId - ID of node to update
   * @param {Object} updates - Properties to update
   * @param {*} [origin=USER_ORIGIN] - Transaction origin (see engine/sync/origins.js)
   * @returns {Object} the per-device fields kept on this device (v2 maps:
   *   collapsed, measured sizes), which change no CRDT: the caller shows them
   */
  updateNode(nodeId, updates, origin = USER_ORIGIN) {
    if (this.isV2()) {
      if (!this.v2.has(nodeId)) {
        console.warn(`[YjsDocManager] Node not found: ${nodeId}`);
        return {};
      }
      // Only what differs is written; per-device fields alone write nothing.
      if (!this.v2.wouldWrite(nodeId, updates)) return this.v2.update(nodeId, updates, null).local;
      let local = {};
      this.doc.transact(() => {
        ({ local } = this.v2.update(nodeId, updates, this._stamp()));
      }, origin);
      return local;
    }
    const existing = this.nodes.get(nodeId);
    if (!existing) {
      console.warn(`[YjsDocManager] Node not found: ${nodeId}`);
      return {};
    }

    this.doc.transact(() => {
      const updated = {
        ...existing,
        ...updates,
        _lastEditedBy: this.userId,
        _lastEditedAt: Date.now(),
      };
      this.nodes.set(nodeId, updated);
    }, origin);
    return {};
  }

  /** Who edited, and when: written with each change of a node. */
  _stamp() {
    return { _lastEditedBy: this.userId, _lastEditedAt: Date.now() };
  }

  /**
   * Update multiple nodes in a single transaction (batch update)
   * This reduces network overhead by sending one Yjs update instead of many
   * @param {Array<{nodeId: string, updates: Object}>} nodeUpdates - Array of updates
   * @param {*} [origin=USER_ORIGIN] - Transaction origin (see engine/sync/origins.js)
   */
  updateNodesBatch(nodeUpdates, origin = USER_ORIGIN) {
    if (!nodeUpdates || nodeUpdates.length === 0) return;

    if (this.isV2()) {
      this.doc.transact(() => {
        const stamp = this._stamp();
        for (const { nodeId, updates } of nodeUpdates) this.v2.update(nodeId, updates, stamp);
      }, origin);
      return;
    }

    this.doc.transact(() => {
      const timestamp = Date.now();
      for (const { nodeId, updates } of nodeUpdates) {
        const existing = this.nodes.get(nodeId);
        if (existing) {
          this.nodes.set(nodeId, {
            ...existing,
            ...updates,
            _lastEditedBy: this.userId,
            _lastEditedAt: timestamp,
          });
        }
      }
    }, origin); // Single transaction = single Yjs "update" event
  }

  /**
   * Add a new node
   * @param {Object} node - Node to add
   * @param {string} parentId - Parent node ID
   * @param {number} index - Position in parent's children (optional)
   * @param {*} [origin=USER_ORIGIN] - Transaction origin (see engine/sync/origins.js)
   */
  addNode(node, parentId, index = -1, origin = USER_ORIGIN) {
    if (this.isV2()) {
      this.doc.transact(() => this.v2.add(node, parentId, index, this._stamp()), origin);
      return;
    }
    this.doc.transact(() => {
      // Recursively add node and all its descendants
      // This is important for cross-boundary moves where a subtree is moved into a shared doc
      const addNodeRecursive = (n, pId) => {
        const flatNode = treeNodeToFlat(n, pId);
        flatNode._lastEditedBy = this.userId;
        flatNode._lastEditedAt = Date.now();
        // Initialize Y.XmlFragment for notes if in yxml-v1 mode
        const notesStr = typeof n.notes === 'string' ? n.notes : '';
        if (this.metadata.get('notesFormat') === 'yxml-v1' && notesStr.trim()) {
          flatNode.notes = '';
          this.setNodeNotesFromMarkdown(n.id, notesStr);
        }
        this.nodes.set(n.id, flatNode);

        // Recursively add children
        (n.children || []).forEach((child) => {
          addNodeRecursive(child, n.id);
        });
      };

      addNodeRecursive(node, parentId);

      // Update parent's childIds to include the new node
      const parent = this.nodes.get(parentId);
      if (parent) {
        const childIds = [...(parent.childIds || [])];
        if (index >= 0 && index < childIds.length) {
          childIds.splice(index, 0, node.id);
        } else {
          childIds.push(node.id);
        }
        this.nodes.set(parentId, { ...parent, childIds });
      }
    }, origin);
  }

  /**
   * Remove a node and its descendants
   * @param {string} nodeId - ID of node to remove
   * @param {*} [origin=USER_ORIGIN] - Transaction origin (see engine/sync/origins.js)
   */
  removeNode(nodeId, origin = USER_ORIGIN) {
    if (this.isV2()) {
      if (!this.v2.has(nodeId)) return;
      this.doc.transact(() => {
        for (const id of this.v2.remove(nodeId)) this._dropNotes(id);
      }, origin);
      return;
    }
    const node = this.nodes.get(nodeId);
    if (!node) return;

    this.doc.transact(() => {
      // What the tree shows under the node goes with it, nothing else: a
      // stale listing of a node shown under another parent (two concurrent
      // moves) does not take it along, and a cycle cannot loop forever.
      const { children } = this._placement();
      const toRemove = new Set();
      const collectDescendants = (id) => {
        if (toRemove.has(id)) return;
        toRemove.add(id);
        (children.get(id) || []).forEach(collectDescendants);
      };
      collectDescendants(nodeId);

      // Remove from parent's childIds
      if (node.parentId) {
        const parent = this.nodes.get(node.parentId);
        if (parent) {
          const childIds = (parent.childIds || []).filter(
            (id) => id !== nodeId,
          );
          this.nodes.set(node.parentId, { ...parent, childIds });
        }
      }

      // Remove all nodes and their notes fragments
      toRemove.forEach((id) => {
        this.nodes.delete(id);
        this._dropNotes(id);
      });
    }, origin);
  }

  /**
   * Clear a deleted node's notes fragment, and its observer and cache. The
   * fragment joins what Ctrl+Z covers first (extendUndoScope, set by
   * engine/core/undo-manager.js): undoing the delete brings the notes back
   * with the node.
   */
  _dropNotes(id) {
    const fragment = this._notesFragmentIfAny(id);
    if (fragment && fragment.length > 0) {
      this.extendUndoScope?.(fragment);
      fragment.delete(0, fragment.length);
    }
    this._notesCache.delete(id);
    this._dirtyNotes.delete(id);
    const obs = this._notesObservers.get(id);
    if (obs) { fragment?.unobserveDeep(obs); this._notesObservers.delete(id); }
  }

  /**
   * Bring this map back to how a version had it (map history,
   * docs/plans/2026-10-21-map-history.md), as one change of this user's (one
   * Ctrl+Z step; the version before stays in the history).
   * - ids null (the owner's full revert): every item as the version had it,
   *   the items it did not have deleted, the tag definitions as they were.
   * - ids (bring items back): those items as the version had them, with
   *   their notes; one deleted since comes back with its id, under its
   *   parent if it is still there, else at the end of the root; the tag
   *   definitions they use are added if missing. Nothing else changes.
   * Notes are copied as they were (rich content included). Schema v2 only.
   * @param {Y.Doc} version - the version, rebuilt (engine/history.js)
   * @param {{ids?: string[]|null}} [options]
   * @returns {{written: number, removed: number}}
   */
  restoreFrom(version, { ids = null } = {}) {
    if (!this.isV2()) throw new Error("This map must be opened once more before it can be restored");
    const { rootId, flat, children } = readMap(version);
    if (!rootId || !flat.has(rootId)) throw new Error("This version has no readable map");
    // Where each item of the version shows, and its order among its siblings.
    const place = new Map();
    const walk = (id, parentId) => {
      const siblings = children.get(id) || [];
      const keys = keysBetween(null, null, siblings.length);
      siblings.forEach((child, i) => {
        const order = flat.get(child)?.order;
        place.set(child, { parentId: id, order: isValidOrderKey(order) ? order : keys[i] });
        walk(child, id);
      });
    };
    place.set(rootId, { parentId: null, order: flat.get(rootId)?.order });
    walk(rootId, null);

    const full = ids === null;
    const wanted = new Set();
    if (full) for (const id of place.keys()) wanted.add(id);
    else for (const id of ids) if (place.has(id)) wanted.add(id);
    const target = new Map();
    const live = this.metadata.get("rootId");
    const orphans = [];
    for (const id of wanted) {
      const { parentId, order } = place.get(id);
      const node = { ...flat.get(id), parentId, order };
      if (!full && parentId && !wanted.has(parentId) && !this.v2.has(parentId)) {
        // Its parent is gone: an item still here stays where it is; one
        // deleted since comes back at the end of the root.
        const now = this.v2.get(id);
        if (now) Object.assign(node, { parentId: now.parentId, order: now.order });
        else orphans.push(node);
      }
      if (!full && id === rootId) delete node.parentId;
      target.set(id, node);
    }
    if (orphans.length) {
      const { flat: liveFlat, place: livePlace } = this.v2.state();
      const lastOrder = liveFlat.get((livePlace.children.get(live) || []).at(-1))?.order;
      const keys = keysBetween(isValidOrderKey(lastOrder) ? lastOrder : null, null, orphans.length);
      orphans.forEach((node, i) => Object.assign(node, { parentId: live, order: keys[i] }));
    }
    const legacyNotes = version.getMap("metadata").get("notesFormat") !== "yxml-v1";

    let result = { written: [], removed: [] };
    this.doc.transact(() => {
      result = this.v2.restore(target, { removeOthers: full, stamp: this._stamp() });
      for (const id of result.removed) this._dropNotes(id);
      for (const id of wanted) {
        if (this._restoreNotes(version, id, legacyNotes ? (flat.get(id)?.notes || "") : null)) result.written.push(id);
      }
      const defs = version.getMap("metadata").get("tagDefinitions") || [];
      const current = this.getTagDefinitions();
      if (full) {
        if (JSON.stringify(defs) !== JSON.stringify(current)) this.metadata.set("tagDefinitions", defs);
      } else {
        // A node's tags: { tagId, setAt } (older maps: the id alone).
        const used = new Set([...wanted].flatMap((id) => (flat.get(id)?.tags || [])
          .map((tag) => (typeof tag === "string" ? tag : tag?.tagId))));
        const missing = defs.filter((d) => used.has(d.id) && !current.some((c) => c.id === d.id));
        if (missing.length) this.metadata.set("tagDefinitions", [...current, ...missing]);
      }
      if (full && rootId !== live) this.metadata.set("rootId", rootId);
    }, USER_ORIGIN);
    return { written: new Set(result.written).size, removed: result.removed.length };
  }

  /**
   * A node's notes made as `version` has them; whether they changed.
   * legacy: the version's notes as Markdown (a version from before rich
   * notes), or null.
   */
  _restoreNotes(version, id, legacy) {
    if (this.metadata.get("notesFormat") !== "yxml-v1") return false;
    const name = "notes-" + id;
    const from = version.share.has(name) ? version.getXmlFragment(name) : null;
    const fragment = this.getNodeNotesFragment(id);
    if (typeof legacy === "string") {
      // A version from before rich notes: its notes were Markdown.
      if (this.serializeNodeNotes(id) === legacy) return false;
      this.extendUndoScope?.(fragment);
      this.setNodeNotesFromMarkdown(id, legacy);
      return true;
    }
    if ((from ? from.toString() : "") === fragment.toString()) return false;
    this.extendUndoScope?.(fragment);
    if (fragment.length > 0) fragment.delete(0, fragment.length);
    if (from && from.length > 0) fragment.insert(0, from.toArray().map((child) => child.clone()));
    this._dirtyNotes.add(id);
    return true;
  }

  /**
   * Move a node to a new parent
   * @param {string} nodeId - ID of node to move
   * @param {string} newParentId - ID of new parent
   * @param {number} index - Position in new parent's children
   * @param {*} [origin=USER_ORIGIN] - Transaction origin (see engine/sync/origins.js)
   */
  moveNode(nodeId, newParentId, index = -1, origin = USER_ORIGIN) {
    if (this.isV2()) {
      if (!this.v2.has(nodeId)) return;
      this.doc.transact(() => this.v2.move(nodeId, newParentId, index, this._stamp()), origin);
      return;
    }
    const node = this.nodes.get(nodeId);
    if (!node) return;

    const oldParentId = node.parentId;

    this.doc.transact(() => {
      // Remove from old parent
      if (oldParentId) {
        const oldParent = this.nodes.get(oldParentId);
        if (oldParent) {
          const childIds = (oldParent.childIds || []).filter(
            (id) => id !== nodeId,
          );
          this.nodes.set(oldParentId, { ...oldParent, childIds });
        }
      }

      // Add to new parent
      const newParent = this.nodes.get(newParentId);
      if (newParent) {
        const childIds = [...(newParent.childIds || [])];
        if (index >= 0 && index < childIds.length) {
          childIds.splice(index, 0, nodeId);
        } else {
          childIds.push(nodeId);
        }
        this.nodes.set(newParentId, { ...newParent, childIds });
      }

      // Update node's parentId
      this.nodes.set(nodeId, {
        ...node,
        parentId: newParentId,
        _lastEditedBy: this.userId,
        _lastEditedAt: Date.now(),
      });
    }, origin);
  }

  /**
   * Get the Yjs state vector for sync
   * @returns {Uint8Array}
   */
  getStateVector() {
    return Y.encodeStateVector(this.doc);
  }

  /**
   * Get updates since a given state vector
   * @param {Uint8Array} stateVector
   * @returns {Uint8Array}
   */
  getUpdatesSince(stateVector) {
    return Y.encodeStateAsUpdate(this.doc, stateVector);
  }

  /**
   * Apply updates from another client/server
   * @param {Uint8Array} update
   */
  applyUpdate(update) {
    const notesBefore = this.docNotes.length;
    // Apply the remote update with 'remote' origin
    Y.applyUpdate(this.doc, update, REMOTE_ORIGIN);
    const notesAfter = this.docNotes.length;

    if (notesBefore !== notesAfter) {
      console.log(
        "[YJS Notes] REMOTE UPDATE changed notes:",
        notesBefore,
        "->",
        notesAfter,
      );
    }

    // Notify the app of remote changes
    if (this.onUpdate) {
      const tree = this.toTree();
      this.onUpdate(tree);
    }
  }

  /**
   * Get full document state for initial sync
   * @returns {Uint8Array}
   */
  getFullState() {
    return Y.encodeStateAsUpdate(this.doc);
  }

  /**
   * Subscribe to document updates (for sending to server)
   * @param {Function} callback - Called with update info object
   * @returns {Function} Unsubscribe function
   */
  onDocUpdate(callback) {
    const handler = (update, origin) => {
      // Only send updates for local changes. See engine/sync/origins.js for the
      // full origin taxonomy. IndexedDB persistence passes its instance as
      // the origin (non-string, not in the local-origin set) — filtered out.
      const isLocalChange = isLocalTxOrigin(origin);
      if (isLocalChange) {
        // Send delta directly - 'update' is already the delta from Yjs
        // This is much smaller than full state (~1-17KB vs ~169KB)
        console.log("[YJS] Sending delta to WebSocket callback, size:", update?.length, "bytes");
        callback({ type: "delta", data: update });
      }
    };

    this.doc.on("update", handler);
    return () => this.doc.off("update", handler);
  }

  /**
   * Get full document state for compaction
   * Called periodically to create a new baseline on the server
   * @param {string} reason - Why compaction was triggered
   * @returns {Object} Update info with type 'fullState', data, stateVector, reason
   */
  getFullStateForCompaction(reason = "periodic") {
    return {
      type: "fullState",
      data: this.getFullState(),
      stateVector: this.getStateVector(), // Plaintext, sent alongside encrypted data
      reason: reason,
    };
  }

  /**
   * Check if the Yjs document has any data (nodes)
   * Used to determine if we should load from CRDT or migrate from records table
   * @returns {boolean} True if the document has at least one node
   */
  hasData() {
    return this.nodeCount() > 0;
  }

  /**
   * Get a single node's properties directly from the CRDT.
   * This is the correct way to read persisted node state (vs Store which is a projection).
   * v1: the stored flat node (with parentId, childIds); v2: a copy of its
   * fields (with parentId, order, no childIds) and this device's fields.
   * @param {string} nodeId - The node ID
   * @returns {Object|null} The flat node object, or null if not found
   */
  getNode(nodeId) {
    if (this.isV2()) {
      const node = this.v2.get(nodeId);
      return node ? { ...node, ...this._localFields(nodeId) } : null;
    }
    return this.nodes.get(nodeId) || null;
  }

  /** Whether the map holds this node (v2: and shows it: not deleted with a parent). */
  hasNode(nodeId) {
    return this.isV2() ? this.v2.has(nodeId) : this.nodes.has(nodeId);
  }

  /** Number of nodes the map holds (v2: shown). */
  nodeCount() {
    return this.isV2() ? this.v2.count() : this.nodes.size;
  }

  /** fn(flatNode, id) for each node (see getNode for the shape). */
  forEachNode(fn) {
    if (this.isV2()) this.v2.forEach((node, id) => fn({ ...node, ...this._localFields(id) }, id));
    else this.nodes.forEach((flatNode, id) => fn(flatNode, id));
  }

  /** Whether a Yjs transaction changed nodes, in either schema. */
  touchesNodes(transaction) {
    if (!transaction) return false;
    return transaction.changed.has(this.nodes) || transaction.changed.has(this.v2.map)
      || transaction.changedParentTypes.has(this.v2.map) || transaction.changed.has(this.v2.deleted);
  }

  /** The shared types Ctrl+Z undoes (engine/core/undo-manager.js), in either schema. */
  undoScope() {
    return [this.nodes, this.v2.map, this.v2.deleted];
  }

  /** Whether this map is in schema v2 (map-format.js). */
  isV2() {
    return this.metadata.get("schemaVersion") === SCHEMA_V2;
  }

  /**
   * Move this map to schema v2 (decision 5: automatically, when a v2 client
   * with write access opens it, once synced; the caller takes the backup).
   * One transaction; `nodes` stays as it is. Idempotent.
   * @returns {{migrated: boolean, count: number}}
   */
  migrateToV2() {
    if (this.isV2()) return { migrated: false, count: 0 };
    const rootId = this.metadata.get("rootId");
    if (!rootId || !this.nodes.has(rootId)) return { migrated: false, count: 0 };
    let count = 0;
    this.doc.transact(() => { count = this.v2.migrateFromV1(); }, MIGRATION_ORIGIN);
    this._v1Changed = false; // the migration read v1 as it is
    console.log(`[YJS] Map ${this.docId} moved to schema v2: ${count} nodes`);
    return { migrated: count > 0, count };
  }

  /**
   * Fold in v1 changes no migration saw (MapNodesV2.foldLateV1).
   * @returns {number} nodes changed
   */
  foldLateV1() {
    if (!this.isV2()) return 0;
    let changed = 0;
    this.doc.transact(() => { changed = this.v2.foldLateV1(this._stamp()); }, MIGRATION_ORIGIN);
    if (changed) console.warn(`[YJS] Folded ${changed} late change(s) of an older release into ${this.docId}`);
    return changed;
  }

  /**
   * Move this map back to schema v1 (MapNodesV2.rollbackToV1): before an
   * older release goes back out. One transaction.
   * @returns {{rolledBack: boolean, count: number}}
   */
  rollbackToV1() {
    if (!this.isV2()) return { rolledBack: false, count: 0 };
    let count = 0;
    this.doc.transact(() => { count = this.v2.rollbackToV1(); }, MIGRATION_ORIGIN);
    console.log(`[YJS] Map ${this.docId} moved back to schema v1: ${count} nodes`);
    return { rolledBack: count > 0, count };
  }

  /** Whether this v2 map still holds the old-format copy (`nodes`). */
  hasOldCopy() {
    if (!this.isV2()) return false;
    for (const _ of this.nodes.keys()) return true;
    return false;
  }

  /** When this map moved to v2 (MapNodesV2.convertedAt), or null. */
  convertedAt() {
    return this.isV2() ? this.v2.convertedAt() : null;
  }

  /** The format reported to the server: 1, 2 (v2 with the old copy) or 3 (without). */
  reportedFormat() {
    if (!this.isV2()) return 1;
    return this.hasOldCopy() ? SCHEMA_V2 : REPORTED_V2_WITHOUT_OLD_COPY;
  }

  /**
   * Drop the old-format copy of this v2 map
   * (docs/plans/2026-10-13-drop-schema-v1.md): late v1 changes folded in
   * first; nothing deleted unless v2 accounts for every node of the copy
   * and a second fold changes nothing; this device keeps the folds the copy
   * held; then one transaction that Ctrl+Z does not undo. The caller checks
   * the conversion's age and takes the backup first.
   * @returns {{dropped: boolean, count?: number, reason?: string, missing?: string[]}}
   */
  dropOldCopy() {
    if (!this.hasOldCopy()) return { dropped: false, reason: "none" };
    if (getShareRole(this.docId) === "viewer") return { dropped: false, reason: "viewer" };
    this.foldLateV1();
    this._v1Changed = false;
    if (this.foldLateV1() > 0) {
      console.warn(`[YJS] Old copy of ${this.docId} kept: late changes did not settle`);
      return { dropped: false, reason: "unsettled" };
    }
    const missing = this.v2.oldCopyMissing();
    if (missing.length) {
      console.warn(`[YJS] Old copy of ${this.docId} kept: ${missing.length} node(s) not in the map`, missing.slice(0, 20));
      return { dropped: false, reason: "missing", missing };
    }
    this._keepOldFolds();
    let count = 0;
    this.doc.transact(() => { count = this.v2.dropOldCopy(); }, MIGRATION_ORIGIN);
    this._v1Changed = false;
    console.log(`[YJS] Old copy of ${this.docId} dropped: ${count} entries`);
    return { dropped: true, count };
  }

  /** This device's folds: where it has none of its own, the one the old copy shows. */
  _keepOldFolds() {
    this.nodes.forEach((value, id) => {
      if (!value || typeof value !== "object" || value.collapsed === undefined) return;
      if (this.local.get(id)?.collapsed !== undefined || !this.v2.has(id)) return;
      this.local.set(id, { collapsed: !!value.collapsed });
    });
  }

  /** Write fields without the edit stamp (migrations of old data), in either schema. */
  _setFieldsQuietly(nodeId, fields) {
    if (this.isV2()) {
      this.v2.update(nodeId, fields, null);
      return;
    }
    const existing = this.nodes.get(nodeId);
    if (existing) this.nodes.set(nodeId, { ...existing, ...fields });
  }

  /**
   * Get metadata value
   * @param {string} key - Metadata key
   * @returns {*} The metadata value, or undefined if not set
   */
  getMetadata(key) {
    return this.metadata.get(key);
  }

  /**
   * Set metadata value
   * @param {string} key - Metadata key
   * @param {*} value - Value to set
   */
  setMetadata(key, value) {
    this.doc.transact(() => {
      this.metadata.set(key, value);
    }, USER_ORIGIN);
  }

  /**
   * Get document-level notes from Y.Array, with same-id duplicates
   * collapsed to the newest entry (by `updated` timestamp).
   *
   * Why this matters: every updateDocNote call is a Y.Array delete+insert,
   * which under concurrent peer writes can produce two array entries
   * with the same note id (both peers delete the same item then each
   * inserts their own copy — CRDT correctly keeps both inserts). Without
   * dedup the UI would render duplicate list items for one logical
   * note. The keystroke-frequency `{updated}` write that triggered this
   * was removed in the same change as this dedup, but two title edits
   * landing concurrently can still race — so we keep the defensive
   * filter here. Picks the entry with the latest `updated` so the
   * freshest metadata wins.
   *
   * @returns {Array} Deduped array of note objects.
   */
  getDocNotes() {
    // A deleted note stays deleted, whatever was done to it at the same time.
    const arr = this.docNotes.toArray().filter((note) => !note?.id || !this.deletedDocNotes.has(note.id));
    if (arr.length < 2) return arr;
    const byId = new Map();
    const idless = [];
    for (const note of arr) {
      const id = note?.id;
      if (!id) {
        // Legacy/imported notes can lack an id. They can't be deduped, but
        // dropping them from display/export paths would silently hide user
        // content (the single-note early-return above kept them; the dedup
        // path used to discard them).
        idless.push(note);
        continue;
      }
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, note);
      } else if ((note.updated || '') > (existing.updated || '')) {
        byId.set(id, note);
      }
    }
    return [...byId.values(), ...idless];
  }

  /**
   * Set document-level notes (bulk replacement - use for imports only)
   * For normal operations, use addDocNote/removeDocNote/updateDocNote instead.
   *
   * In yxml-v1 mode the array entry's `content` plain string is no
   * longer the source of truth — each note's body lives in its
   * Y.XmlFragment. setDocNotes is called by the import path AFTER
   * migrateDocNotesToFragments has set the version marker (createMap
   * → openDoc → migration → setDocNotes), so without seeding fragments
   * here, imported notes would land with content in the array but
   * empty fragments — the readers we now have (Day 3 export, Day 4
   * mobile reader, Day 2 static view) all prefer the fragment, so
   * the user would see empty notes after import.
   *
   * @param {Array} notes - Array of note objects
   */
  setDocNotes(notes) {
    if (!Array.isArray(notes)) return;
    const yxmlMode = this.metadata.get('docNotesFormat') === 'yxml-v1';
    this.doc.transact(() => {
      while (this.docNotes.length > 0) {
        this.docNotes.delete(0, 1);
      }
      if (notes.length > 0) {
        notes.forEach((note) => {
          // Imported on purpose: shown even if a note of that id was deleted.
          if (note?.id) this.deletedDocNotes.delete(note.id);
          if (yxmlMode) {
            // Strip content from the array entry and route it into the
            // fragment. Same pattern as addDocNote's yxml-v1 path.
            const initialContent = typeof note?.content === 'string' ? note.content : '';
            const { content: _ignored, ...metaOnly } = note || {};
            this.docNotes.push([{ ...metaOnly, content: "" }]);
            if (note?.id && initialContent.trim()) {
              try {
                this.setDocNoteFromMarkdown(note.id, initialContent);
              } catch (err) {
                console.warn("[YJS Notes] setDocNotes: failed to seed fragment for", note?.id, err);
              }
            }
          } else {
            this.docNotes.push([note]);
          }
        });
      }
    }, IMPORT_ORIGIN);
  }

  /**
   * Add a single document note (preferred for normal operations).
   *
   * In yxml-v1 mode the array entry stores only metadata (id, title,
   * timestamps, refnotes) — the rich-text body lives in the per-note
   * Y.XmlFragment `docnote-<id>`. If an `initialContent` markdown
   * string is supplied (typical for imports of older exports), it's
   * seeded into the fragment via setDocNoteFromMarkdown; we clear it
   * out of the array entry so older clients won't accidentally write
   * it back. In legacy mode (pre-migration docs) we just push the
   * entry verbatim.
   *
   * @param {Object} note - Note object with id, title, content, created, updated, refnotes
   */
  addDocNote(note) {
    console.log("[YJS Notes] addDocNote START:", note.id, "title:", note.title?.substring(0, 20));
    const yxmlMode = this.metadata.get('docNotesFormat') === 'yxml-v1';
    const initialContent = typeof note?.content === 'string' ? note.content : '';
    this.doc.transact(() => {
      // Added on purpose (moved back, imported again): shown even if a note
      // of that id was deleted.
      if (note?.id) this.deletedDocNotes.delete(note.id);
      if (yxmlMode) {
        // Strip the plain-string body before pushing — fragment is the
        // sole authority. Seed the fragment from the supplied content,
        // if any.
        const { content: _ignored, ...metaOnly } = note || {};
        this.docNotes.push([{ ...metaOnly, content: "" }]);
        if (note?.id && initialContent.trim()) {
          try {
            this.setDocNoteFromMarkdown(note.id, initialContent);
          } catch (err) {
            console.warn("[YJS Notes] addDocNote: failed to populate fragment:", err);
          }
        }
      } else {
        this.docNotes.push([note]);
      }
      console.log("[YJS Notes] addDocNote TRANSACTION complete, array length:", this.docNotes.length);
    }, USER_ORIGIN);
    console.log("[YJS Notes] addDocNote END - IndexedDB persistence queued automatically by y-indexeddb");
  }

  /** The positions of a document note's entries in the array (usually one). */
  _docNoteIndices(noteId) {
    const indices = [];
    this.docNotes.toArray().forEach((n, i) => { if (n?.id === noteId) indices.push(i); });
    return indices;
  }

  /**
   * Remove a document note by ID
   * @param {string} noteId - ID of the note to remove
   * @returns {boolean} True if note was found and removed
   */
  removeDocNote(noteId) {
    console.log("[YJS Notes] removeDocNote START:", noteId);
    const indices = this._docNoteIndices(noteId);
    if (indices.length) {
      this.doc.transact(() => {
        // Every entry of it: two people changing a note at once leave two
        // (updateDocNote replaces its entry), and removing only one brought
        // the note back, empty (simulation, 2026-10-26).
        for (const index of indices.reverse()) this.docNotes.delete(index, 1);
        // And kept as deleted: a rename made at the same time elsewhere
        // writes a new entry, which then stays hidden (getDocNotes).
        this.deletedDocNotes.set(noteId, Date.now());
        // Also clear the fragment if it has content. The fragment name
        // persists in the Y.Doc but content goes empty, mirroring how
        // node-notes deletion is handled. Cheap and idempotent.
        try {
          const fragment = this.getDocNoteFragment(noteId);
          if (fragment.length > 0) {
            fragment.delete(0, fragment.length);
          }
        } catch (err) {
          console.warn("[YJS Notes] removeDocNote: failed to clear fragment:", err);
        }
        console.log("[YJS Notes] removeDocNote TRANSACTION complete, array length:", this.docNotes.length);
      }, USER_ORIGIN);
      console.log("[YJS Notes] removeDocNote END - IndexedDB persistence queued");
      return true;
    }
    console.log("[YJS Notes] removeDocNote - note not found:", noteId);
    return false;
  }

  /**
   * Update a document note by ID
   * @param {string} noteId - ID of the note to update
   * @param {Object} updates - Properties to update (title, content, updated, refnotes)
   * @returns {boolean} True if note was found and updated
   */
  updateDocNote(noteId, updates) {
    console.log("[YJS Notes] updateDocNote START:", noteId, "updates:", Object.keys(updates));
    if (this.deletedDocNotes.has(noteId)) return false; // deleted meanwhile (getDocNotes)
    const arr = this.docNotes.toArray();
    const indices = this._docNoteIndices(noteId);
    const index = indices[0] ?? -1;
    if (index >= 0) {
      // From the freshest of its entries (the one getDocNotes shows); the
      // others go in the same change, so duplicates do not pile up.
      const note = indices.map((i) => arr[i]).reduce((a, b) => ((b.updated || "") > (a.updated || "") ? b : a));
      const removeDuplicates = () => {
        for (const i of indices.slice(1).reverse()) this.docNotes.delete(i, 1);
      };
      const yxmlMode = this.metadata.get('docNotesFormat') === 'yxml-v1';
      const contentChanged = Object.prototype.hasOwnProperty.call(updates, 'content');
      const newContent = typeof updates.content === 'string' ? updates.content : '';
      this.doc.transact(() => {
        if (yxmlMode) {
          // Fragment is the sole authority for content. We strip `content`
          // out of the array update so concurrent peer edits to the
          // fragment aren't clobbered by a stale string reaching this
          // path (e.g., a recovery-stash save). Title/refnotes/timestamps
          // flow through the array as normal.
          const { content: _content, ...metaUpdates } = updates;
          removeDuplicates();
          this.docNotes.delete(index, 1);
          this.docNotes.insert(index, [{ ...note, ...metaUpdates, content: "" }]);
          if (contentChanged) {
            // Caller (legacy / recovery-stash path) explicitly passed a
            // string body. Treat it as an authoritative replace — same
            // clobber risk that pre-Day-5 dual-write had, but only on
            // the explicit ask. The collab editor path does NOT pass
            // content here; it writes ops directly to the fragment.
            try {
              this.setDocNoteFromMarkdown(noteId, newContent);
            } catch (err) {
              console.warn("[YJS Notes] updateDocNote: failed to update fragment:", err);
            }
          }
        } else {
          // Legacy mode (pre-migration doc): old behavior unchanged.
          removeDuplicates();
          this.docNotes.delete(index, 1);
          this.docNotes.insert(index, [{ ...note, ...updates }]);
        }
        console.log("[YJS Notes] updateDocNote TRANSACTION complete, array length:", this.docNotes.length);
      }, USER_ORIGIN);
      console.log("[YJS Notes] updateDocNote END - IndexedDB persistence queued");
      return true;
    }
    console.log("[YJS Notes] updateDocNote - note not found:", noteId);
    return false;
  }

  // ---- Node Notes Y.XmlFragment Methods ----

  /**
   * Get the Y.XmlFragment for a node's notes.
   * Fragments are created lazily by Yjs on first access.
   * @param {string} nodeId
   * @returns {Y.XmlFragment}
   */
  getNodeNotesFragment(nodeId) {
    // Whoever binds an editor to it gets it here: watch it, so its edits
    // reach the Store's copy of the notes (refreshDirtyNotes).
    this._observeFragment(nodeId);
    return this.doc.getXmlFragment('notes-' + nodeId);
  }

  /**
   * The notes edited since the last tree build, serialized again: an editor
   * writes a note's fragment directly, and the tree (the Store's copy) is
   * not rebuilt per keystroke. Returns nodeId → markdown ('' when emptied);
   * the caller updates the Store's nodes with it.
   * @returns {Map<string, string>}
   */
  refreshDirtyNotes() {
    const out = new Map();
    if (this.metadata.get('notesFormat') !== 'yxml-v1') return out;
    for (const id of [...this._dirtyNotes]) {
      const fragment = this.doc.getXmlFragment('notes-' + id);
      try {
        const markdown = fragment.length > 0 ? this.serializeNodeNotes(id) : '';
        if (fragment.length > 0) this._notesCache.set(id, markdown);
        else this._notesCache.delete(id);
        this._dirtyNotes.delete(id);
        out.set(id, markdown);
      } catch (err) {
        // Left dirty: the next tree build tries again.
        console.warn('[YJS] Failed to serialize notes fragment for', id, err);
      }
    }
    return out;
  }

  /**
   * Set up a change observer on a fragment for dirty-tracking.
   * @param {string} nodeId
   */
  _observeFragment(nodeId) {
    if (this._notesObservers.has(nodeId)) return; // already observing
    const fragment = this.doc.getXmlFragment('notes-' + nodeId);
    const handler = () => {
      this._dirtyNotes.add(nodeId);
    };
    fragment.observeDeep(handler);
    this._notesObservers.set(nodeId, handler);
  }

  /**
   * Serialize a node's Y.XmlFragment to a markdown string.
   * Returns '' if the fragment is empty.
   * @param {string} nodeId
   * @returns {string}
   */
  serializeNodeNotes(nodeId) {
    const fragment = this.getNodeNotesFragment(nodeId);
    return fragmentToMarkdown(fragment);
  }

  /**
   * Initialize a node's Y.XmlFragment from a markdown string.
   * Clears any existing content in the fragment first.
   * @param {string} nodeId
   * @param {string} markdown
   */
  setNodeNotesFromMarkdown(nodeId, markdown) {
    const fragment = this.getNodeNotesFragment(nodeId);
    markdownToFragment(fragment, markdown, this.doc);
    // Update cache directly (avoid re-serializing what we just wrote)
    this._notesCache.set(nodeId, markdown || '');
    this._dirtyNotes.delete(nodeId);
    this._observeFragment(nodeId);
  }

  /**
   * Clear a node's Y.XmlFragment content (for deletion cleanup).
   * The fragment name persists in the Y.Doc but content is empty.
   * @param {string} nodeId
   */
  clearNodeNotesFragment(nodeId) {
    const fragment = this.getNodeNotesFragment(nodeId);
    if (fragment.length > 0) {
      this.doc.transact(() => {
        fragment.delete(0, fragment.length);
      }, USER_ORIGIN);
    }
  }

  /**
   * Check if a node has a non-empty Y.XmlFragment for notes.
   * @param {string} nodeId
   * @returns {boolean}
   */
  hasNodeNotesFragment(nodeId) {
    const fragment = this._notesFragmentIfAny(nodeId);
    return !!fragment && fragment.length > 0;
  }

  // ---- Doc Notes Y.XmlFragment Methods ----
  // Mirror of the node-notes API but keyed by docNote id and using a
  // distinct fragment-name prefix (`docnote-`) so node ids and docNote ids
  // can never collide. Doc notes live in a Y.Array (see this.docNotes); the
  // metadata fields stay in that array, but the rich-text content moves
  // here once the doc is migrated to docNotesFormat='yxml-v1'. During the
  // bridge release the array's plain `content` string is also kept so
  // older clients can still read.

  /**
   * Get the Y.XmlFragment for a doc note's body.
   * Fragments are created lazily by Yjs on first access.
   * @param {string} noteId
   * @returns {Y.XmlFragment}
   */
  getDocNoteFragment(noteId) {
    return this.doc.getXmlFragment('docnote-' + noteId);
  }

  /**
   * Serialize a docNote's Y.XmlFragment to a markdown string.
   * Returns '' if the fragment is empty.
   * @param {string} noteId
   * @returns {string}
   */
  serializeDocNote(noteId) {
    const fragment = this.getDocNoteFragment(noteId);
    return fragmentToMarkdown(fragment);
  }

  /**
   * Serialize a docNote's Y.XmlFragment directly to HTML — preserves trailing
   * hardBreaks and empty paragraphs that the markdown round-trip would lose.
   * Use this for static display of collab notes; markdown export should still
   * call serializeDocNote.
   *
   * @param {string} noteId
   * @returns {string} HTML, or '' if the fragment is empty.
   */
  serializeDocNoteHtml(noteId) {
    const fragment = this.getDocNoteFragment(noteId);
    return fragmentToHtml(fragment);
  }

  /**
   * Initialize a docNote's Y.XmlFragment from a markdown string.
   * Clears any existing content in the fragment first.
   * @param {string} noteId
   * @param {string} markdown
   */
  setDocNoteFromMarkdown(noteId, markdown) {
    const fragment = this.getDocNoteFragment(noteId);
    markdownToFragment(fragment, markdown, this.doc);
  }

  /**
   * Clear a docNote's Y.XmlFragment content (for deletion cleanup).
   * @param {string} noteId
   */
  clearDocNoteFragment(noteId) {
    const fragment = this.getDocNoteFragment(noteId);
    if (fragment.length > 0) {
      this.doc.transact(() => {
        fragment.delete(0, fragment.length);
      }, USER_ORIGIN);
    }
  }

  /**
   * Check if a docNote has a non-empty Y.XmlFragment for content.
   * @param {string} noteId
   * @returns {boolean}
   */
  hasDocNoteFragment(noteId) {
    const fragment = this.doc.getXmlFragment('docnote-' + noteId);
    return fragment.length > 0;
  }


  // ============================================
  // Tag Definitions
  // ============================================

  /**
   * Get all tag definitions for this document.
   * @returns {Array<{id: string, name: string, color: string}>}
   */
  getTagDefinitions() {
    return this.metadata.get('tagDefinitions') || [];
  }

  /** Stored tag categories (engine/core/tag-filter.js); empty = built-ins only. */
  getTagCategories() {
    const categories = this.metadata.get('tagCategories');
    return Array.isArray(categories) ? categories : [];
  }

  /**
   * Set all tag definitions (replaces the entire list).
   * @param {Array<{id: string, name: string, color: string}>} definitions
   */
  setTagDefinitions(definitions) {
    this.doc.transact(() => {
      this.metadata.set('tagDefinitions', definitions);
    }, USER_ORIGIN);
  }

  /**
   * Initialize default tag definitions if none exist.
   * Ensures mandatory tags (Todo, In Progress, Done) always exist.
   * Called on doc open. Idempotent.
   */
  initDefaultTags(generateId) {
    const existing = this.metadata.get('tagDefinitions') || [];
    const mandatoryNames = ['Todo', 'In Progress', 'Done'];
    const existingNames = existing.map(t => t.name);
    const missing = mandatoryNames.filter(n => !existingNames.includes(n));

    // Always upgrade existing mandatory-named tags to have the flag
    const needsUpgrade = existing.some(t =>
      mandatoryNames.includes(t.name) && !t.mandatory
    );

    if (missing.length === 0 && !needsUpgrade) return;

    const upgraded = existing.map(t =>
      mandatoryNames.includes(t.name) ? { ...t, mandatory: true } : t
    );
    for (const name of missing) {
      upgraded.push({ id: generateId(), name, mandatory: true });
    }
    this.setTagDefinitions(upgraded);
  }

  /**
   * Migrate node status fields to tags.
   * Converts status values (todo/in-progress/done) to corresponding mandatory tags.
   * Idempotent: checks metadata flag.
   */
  migrateStatusToTags() {
    const alreadyFlagged = !!this.metadata.get('statusMigratedToTags');

    const defs = this.metadata.get('tagDefinitions') || [];
    const statusToTag = {};
    for (const def of defs) {
      if (def.name === 'Todo') statusToTag['todo'] = def.id;
      if (def.name === 'In Progress') statusToTag['in-progress'] = def.id;
      if (def.name === 'Done') statusToTag['done'] = def.id;
    }

    // Don't proceed if tag definitions are missing —
    // they may not have been created yet (initDefaultTags not called or not synced).
    if (!statusToTag['todo'] || !statusToTag['in-progress'] || !statusToTag['done']) {
      console.warn('[YJS] Status->tags migration deferred: tag definitions incomplete',
        'have:', Object.keys(statusToTag), 'defs:', defs.length);
      return;
    }

    // Self-healing: even if the flag was set, verify no nodes were missed.
    // A previous run may have set the flag while tag definitions were empty.
    if (alreadyFlagged) {
      let unmigrated = 0;
      this.forEachNode((flatNode) => {
        if (!flatNode.status) return;
        const tagId = statusToTag[flatNode.status];
        if (!tagId) return;
        const tags = flatNode.tags || [];
        const has = tags.some(t =>
          typeof t === 'string' ? t === tagId : t.tagId === tagId
        );
        if (!has) unmigrated++;
      });
      if (unmigrated === 0) {
        console.log('[YJS] Status->tags migration already done, verified OK');
        return;
      }
      console.warn(`[YJS] Status->tags flag was set but ${unmigrated} nodes still need migration — re-running`);
    }

    let migrated = 0;
    let withStatus = 0;
    this.doc.transact(() => {
      this.forEachNode((flatNode, nodeId) => {
        if (!flatNode.status) return;
        withStatus++;
        const tagId = statusToTag[flatNode.status];
        if (!tagId) {
          console.warn('[YJS] Unknown status value:', flatNode.status, 'on node:', nodeId);
          return;
        }

        const tags = flatNode.tags || [];
        const alreadyHas = tags.some(t =>
          typeof t === 'string' ? t === tagId : t.tagId === tagId
        );
        if (alreadyHas) return;

        const newTag = { tagId, setAt: new Date().toISOString() };
        this._setFieldsQuietly(nodeId, { tags: [...tags, newTag] });
        migrated++;
      });
      this.metadata.set('statusMigratedToTags', true);
    }, MIGRATION_ORIGIN);

    console.log(`[YJS] Status->tags migration complete: ${migrated} migrated, ${withStatus} had status, ${this.nodeCount()} total nodes`);
  }

  /**
   * Migrate notes from old storage locations to Y.Array
   * Should be called after persistence is synced.
   * Checks both metadata.docNotes and root.notes for migration.
   *
   * Note locations (in priority order):
   * 1. Y.Array "docNotes" - new CRDT-safe location (preferred)
   * 2. metadata.docNotes - partial fix location (needs migration)
   * 3. root.notes - original location in tree (needs migration)
   */
  migrateDocNotes() {
    const yArrayNotes = this.docNotes.toArray();

    // Check old locations
    const metaNotes = this.metadata.get("docNotes");
    const metaArray =
      metaNotes && Array.isArray(metaNotes) ? metaNotes : [];

    const rootId = this.metadata.get("rootId");
    const rootNode = rootId ? this.getNode(rootId) : null;
    const rootNotes = rootNode?.notes;
    const rootArray =
      rootNotes && Array.isArray(rootNotes) ? rootNotes : [];

    console.log("[YJS Notes] === Migration check ===");
    console.log("[YJS Notes]   Y.Array docNotes:", yArrayNotes.length, "notes");
    console.log("[YJS Notes]   metadata.docNotes:", metaArray.length, "notes");
    console.log("[YJS Notes]   root.notes:", rootArray.length, "notes");

    // Skip if no old locations have data
    if (metaArray.length === 0 && rootArray.length === 0) {
      console.log("[YJS Notes]   -> No migration needed, no old data");
      return;
    }

    // Union merge: collect all notes from all locations, deduplicate by ID
    const noteMap = new Map();

    // Add Y.Array notes first (current/preferred)
    yArrayNotes.forEach((note) => {
      if (note?.id) noteMap.set(note.id, note);
    });

    // Merge metadata notes (add only if not already present)
    let metaMerged = 0;
    metaArray.forEach((note) => {
      if (note?.id && !noteMap.has(note.id)) {
        noteMap.set(note.id, note);
        metaMerged++;
      }
    });

    // Merge root notes (add only if not already present)
    let rootMerged = 0;
    rootArray.forEach((note) => {
      if (note?.id && !noteMap.has(note.id)) {
        noteMap.set(note.id, note);
        rootMerged++;
      }
    });

    const totalUnique = noteMap.size;
    const needsUpdate = totalUnique > yArrayNotes.length;

    console.log(
      "[YJS Notes]   Union merge: total unique =", totalUnique,
      "(merged", metaMerged, "from metadata,", rootMerged, "from root.notes)",
    );

    if (!needsUpdate && metaArray.length === 0 && rootArray.length === 0) {
      console.log("[YJS Notes]   -> No migration needed, Y.Array is current");
      return;
    }

    this.doc.transact(() => {
      // Update Y.Array if union added new notes
      if (needsUpdate) {
        while (this.docNotes.length > 0) {
          this.docNotes.delete(0, 1);
        }
        // Sort by created date to maintain order
        const allNotes = Array.from(noteMap.values()).sort(
          (a, b) => new Date(a.created || 0) - new Date(b.created || 0),
        );
        allNotes.forEach((note) => this.docNotes.push([note]));
      }

      // Clean up old locations regardless (data is now in Y.Array)
      if (metaArray.length > 0) {
        this.metadata.delete("docNotes");
        console.log("[YJS Notes]   -> Cleaned metadata.docNotes");
      }
      if (rootArray.length > 0) {
        this._setFieldsQuietly(rootId, { notes: [] });
        console.log("[YJS Notes]   -> Cleaned root.notes");
      }
    }, MIGRATION_ORIGIN);

    console.log("[YJS Notes]   Final Y.Array count:", this.docNotes.length);
    console.log("[YJS Notes] === End migration ===");
  }

  /**
   * Migrate doc notes from 'tags' property to 'refnotes'.
   * Renames the property on each doc note to free up 'tags' for node tagging.
   * Idempotent: checks metadata flag and skips if already done.
   */
  migrateDocNoteRefnotes() {
    if (this.metadata.get('docNoteRefnoteMigrated')) return;

    const notes = this.docNotes.toArray();
    let migrated = 0;

    this.doc.transact(() => {
      for (let i = 0; i < notes.length; i++) {
        const note = notes[i];
        if (note.tags && !note.refnotes) {
          const updated = { ...note, refnotes: note.tags };
          delete updated.tags;
          this.docNotes.delete(i, 1);
          this.docNotes.insert(i, [updated]);
          migrated++;
        }
      }
      this.metadata.set('docNoteRefnoteMigrated', true);
    }, MIGRATION_ORIGIN);

    if (migrated > 0) {
      console.log(`[YJS Notes] Migrated ${migrated} doc notes: tags → refnotes`);
    }
  }

  /**
   * Migrate node notes from flat-node string property to Y.XmlFragment.
   * Each node with non-empty notes gets a Y.XmlFragment initialized from
   * the markdown string. The string property is then cleared.
   *
   * @param {Function} [progressCallback] - (current, total) => void
   * @returns {{ migrated: boolean, count: number }}
   */
  migrateNodeNotes(progressCallback = null) {
    const format = this.metadata.get('notesFormat');
    if (format === 'yxml-v1') {
      console.log('[YJS] Node notes already migrated to Y.XmlFragment');
      return { migrated: false, count: 0 };
    }

    console.log('[YJS] === Begin node notes migration to Y.XmlFragment ===');

    // Collect nodes with string notes
    const toMigrate = [];
    this.forEachNode((flatNode, id) => {
      const notes = flatNode.notes;
      // notes can be a string or legacy array — only migrate non-empty strings
      if (typeof notes === 'string' && notes.trim().length > 0) {
        toMigrate.push({ id, notes });
      }
    });

    console.log(`[YJS] Found ${toMigrate.length} nodes with notes to migrate`);

    if (toMigrate.length === 0) {
      this.doc.transact(() => {
        this.metadata.set('notesFormat', 'yxml-v1');
      }, MIGRATION_ORIGIN);
      console.log('[YJS] No notes to migrate, set version marker');
      return { migrated: true, count: 0 };
    }

    // Perform migration in a single transaction (atomic)
    this.doc.transact(() => {
      for (let i = 0; i < toMigrate.length; i++) {
        const { id, notes } = toMigrate[i];
        let converted = false;
        try {
          // Initialize Y.XmlFragment from markdown
          // (setNodeNotesFromMarkdown uses markdownToFragment which supports nested transacts)
          this.setNodeNotesFromMarkdown(id, notes);
          converted = true;
        } catch (err) {
          console.warn(`[YJS] Failed to migrate notes for node ${id}:`, err);
          // Continue with other nodes — partial migration is better than none
        }

        // Clear string notes from flat node — but ONLY if the fragment now
        // holds the content. The string is the sole copy; clearing it after a
        // failed conversion would permanently destroy the note (and sync the
        // destruction to all peers). Dual-read prefers the fragment, so a
        // leftover string on a failed node is harmless.
        if (converted) {
          this._setFieldsQuietly(id, { notes: '' });
        }

        if (progressCallback) progressCallback(i + 1, toMigrate.length);
      }

      // Set version marker
      this.metadata.set('notesFormat', 'yxml-v1');
    }, MIGRATION_ORIGIN);

    console.log(`[YJS] === Migration complete: ${toMigrate.length} nodes migrated ===`);
    return { migrated: true, count: toMigrate.length };
  }

  /**
   * Migrate doc-notes content from plain string to Y.XmlFragment (additive).
   *
   * Distinct from the older `migrateDocNotes()` above, which moves notes
   * from legacy `metadata.docNotes` / `root.notes` storage into the
   * Y.Array. This one moves each note's CONTENT string into a per-note
   * Y.XmlFragment so collaborative editor bindings (ySyncPlugin) get
   * char-level CRDT merge instead of last-write-wins.
   *
   * Bridge-release semantics: this populates fragments alongside the
   * existing array entries' `content` strings — it does NOT clear the
   * legacy strings. Older clients keep reading `content`; newer clients
   * with editor-binding via ySyncPlugin will read from the fragment. A
   * follow-up release drops the legacy strings once the fleet is
   * confirmed on the bridge build.
   *
   * Idempotent: short-circuits when metadata.docNotesFormat === 'yxml-v1'.
   *
   * @param {Function} [progressCallback] - (current, total) => void
   * @returns {{ migrated: boolean, count: number }}
   */
  migrateDocNotesToFragments(progressCallback = null) {
    const format = this.metadata.get('docNotesFormat');
    if (format === 'yxml-v1') {
      console.log('[YJS] Doc notes already migrated to Y.XmlFragment');
      return { migrated: false, count: 0 };
    }

    console.log('[YJS] === Begin doc notes migration to Y.XmlFragment ===');

    // Collect notes that have a plain-string `content` worth migrating.
    const toMigrate = [];
    const arr = this.docNotes.toArray();
    for (const note of arr) {
      if (!note?.id) continue;
      const content = typeof note.content === 'string' ? note.content : '';
      if (content.trim().length > 0) {
        toMigrate.push({ id: note.id, content });
      }
    }

    console.log(`[YJS] Found ${toMigrate.length} doc notes with content to migrate`);

    if (toMigrate.length === 0) {
      this.doc.transact(() => {
        this.metadata.set('docNotesFormat', 'yxml-v1');
      }, MIGRATION_ORIGIN);
      console.log('[YJS] No doc notes to migrate, set version marker');
      return { migrated: true, count: 0 };
    }

    // Single transaction for atomicity.
    this.doc.transact(() => {
      for (let i = 0; i < toMigrate.length; i++) {
        const { id, content } = toMigrate[i];
        try {
          this.setDocNoteFromMarkdown(id, content);
        } catch (err) {
          console.warn(`[YJS] Failed to migrate doc note ${id}:`, err);
          // Continue with other notes — partial migration is preferable to none.
        }

        // Bridge release: leave the array entry's `content` string untouched.
        // Older clients still read it; newer clients prefer the fragment.

        if (progressCallback) progressCallback(i + 1, toMigrate.length);
      }

      this.metadata.set('docNotesFormat', 'yxml-v1');
    }, MIGRATION_ORIGIN);

    console.log(`[YJS] === Doc notes migration complete: ${toMigrate.length} notes migrated ===`);
    return { migrated: true, count: toMigrate.length };
  }

  /**
   * Backup the current Y.Doc state for migration safety.
   * @returns {Uint8Array} Encoded state that can be restored
   */
  createBackup() {
    return Y.encodeStateAsUpdate(this.doc);
  }

  // NOTE: restoreFromBackup() was removed (audit, 2026-06-12). Dead code
  // with a latent bug: re-attaching IndexeddbPersistence to the uncleaned
  // `orbital-<docId>` store merged the old state back in, making the
  // restore a no-op union. createBackup() above is still used (crdt-bridge).

  /**
   * Clean up resources
   */
  async destroy() {
    if (this.persistence) {
      await this.persistence.destroy();
      this._stopPersistenceMonitor?.();
      this._stopPersistenceMonitor = null;
    }

    // Clean up fragment observers
    for (const [id, handler] of this._notesObservers) {
      try { this.doc.getXmlFragment('notes-' + id).unobserveDeep(handler); } catch {}
    }
    this._notesObservers.clear();
    this._notesCache.clear();
    this._dirtyNotes.clear();

    this._stopSizeWatch?.();
    this._stopSizeWatch = null;
    this.doc.destroy();
  }
}
